import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useContext,
} from "react";
import { useAuth0 } from "react-auth0-spa";
import axios from "axios";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Loader from "../../../components/Loader/Loader";
import { getFromCache, cache } from "../cache";
import moment from "moment";
import {
  appointments,
  IAppointments,
  PatientClinic,
} from "types/calender/appointments.js";
import CalenderView from "./CalenderView";
import { isMobile } from "react-device-detect";
import ClientActivities from "../../../components/clientSupportingComponents/ClientActivities";
import HelpCard from "../../../components/clientSupportingComponents/HelpCard";
import { ApplicationStages } from "../../../constant/AppConstants";
import { clientClinics } from "../../../components/forms/providerAppointmentForms/AllAppointmentsFunction";
import { ApiCalls } from "../../../components/forms/providerAppointmentForms/AllAppointmentsApi";
import { myContext } from "../../../hooks/useContext/useContext";

import { useWebSocket } from "../../../utils/WebSockets/WebSocketProvider";
import ClientRescheduleError from "../../../components/sharedComponents/Error/ClientRescheduleError";
import ContactDrawer from "../../../components/Contact/ContactDrawer";
import { useNavigate, useLocation } from "react-router-dom";
import ClientDocuments from "../../../components/clientSupportingComponents/clientDocuments/ClientDocuments";
import MessageCard from "../../../components/patientPortal/MessageCard";
import PageFrame from "../../../components/patientPortal/PageFrame";
import {
  CaregiverClient,
  mergeCaregiverClients,
  readCaregiverClients,
  writeCaregiverClients,
} from "./caregiverClientsCache";

const CACHED_APPOINTMENTS_KEY: String = "appointments";
const LAST_UPDATED_FORMAT = "h:mm A";

interface LocationState {
  scrollToForm?: boolean | undefined;
}

export interface LoadedRange {
  start: string;
  end: string;
}

// "Maya", "Maya and Leo", "Maya, Leo and Sam"
export const formatNameList = (names: string[]) => {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const Appointment: React.FC<{}> = () => {
  const { loading, user, getTokenSilently, logout } = useAuth0();
  const { pdfViewerOpen } = useContext(myContext);
  const location = useLocation();
  const { scrollToForm } = (location.state as LocationState) || {};
  const navigate = useNavigate();
  // Links elsewhere still ask Home to "scroll to the form"; the form now lives in a drawer.
  const [contactOpen, setContactOpen] = useState<boolean>(!!scrollToForm);
  const [lastUpdated, setLastUpdated] = useState<moment.Moment | null>(null);
  const openContact = useCallback(() => setContactOpen(true), []);

  useEffect(() => {
    if (user["https://corticacare.com/app_metadata"].provider) {
      logout();
    }
  }, [user]);

  const pollingTime = 30000;

  const [appointments, setAppointments] = useState<appointments[]>(
    getFromCache(CACHED_APPOINTMENTS_KEY) || []
  );
  const [backendError, setBackendError] = useState<Boolean>(false);
  const [clinicBackendError, setClinicBackendError] = useState<Boolean>(false);
  const [softloading, setsoftloading] = useState<boolean>(false);
  const [patientClinic, setPatientClinic] = useState<number[]>([]);
  const [hasComponentMounted, setComponentMounted] = useState<boolean>(true);
  const [pollCount, setPollCount] = useState<number>(0);
  const [freezePoll, setFreezPoll] = useState<boolean>(false);
  const [StartDate, setStartDate] = useState<any>();
  const [endDate, setEndDate] = useState<any>();
  const pollCountRef = useRef<number>(0);
  // Numbers each request so only the latest reply is used, even for the same day.
  const latestRequestRef = useRef<number>(0);
  const [syncActionError, setSyncActionError] = useState<boolean>(false);
  const [syncActionMsg, setSyncActionMsg] = useState<string>("update");
  const [syncActionDate, setSyncActionDate] = useState<string>();

  const [noApptsRecord, setApptsRecords] = useState<Boolean>(false);
  // The range of the latest request once it has finished, however it ended.
  const [loadedRange, setLoadedRange] = useState<LoadedRange | null>(null);

  const { webSocketData } = useWebSocket();

  const [displayBanner, setDisplayBanner] = useState<boolean>(false);

  useEffect(() => {
    if (webSocketData?.athena || webSocketData?.cr) {
      if (webSocketData.athena !== true || webSocketData.cr !== true) {
        setDisplayBanner(true);
      } else {
        setDisplayBanner(false);
      }
    } else {
      setDisplayBanner(false);
    }
  }, [webSocketData]);

  const showClientActivities = false;

  useEffect(() => {
    navigate(location.pathname, { replace: true, state: {} });
  }, [navigate, location.pathname]);

  const getAppointments = useCallback(
    async (startDate: string, endDate: string, setLoading: boolean) => {
      const requestId = ++latestRequestRef.current;
      setStartDate(startDate);
      setEndDate(endDate);

      const payLoad = {
        user_id: user.sub,
        isProvider: false,
        startDate: startDate,
        endDate: endDate,
        email: user.email,
      };

      const apiUrl =
        process.env.REACT_APP_STAGE === ApplicationStages.local
          ? `http://localhost:8000/appointments`
          : `${process.env.REACT_APP_BACKEND_API}/appointments`;

      setsoftloading(setLoading);
      try {
        // Inside the try: a failed token request shows the error banner
        // instead of leaving the schedule loading forever.
        const accessToken = await getTokenSilently();
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };
        const resp = await axios.post(apiUrl, payLoad, config);
        const errorMessages: { [key: number]: string } = {
          401: "create",
          402: "update",
          403: "cancel",
        };

        let results: appointments[] = [];
        if (!Array.isArray(resp.data)) {
          throw new Error(
            "Appointments reply was not a list; showing the error banner instead of an empty day."
          );
        }
        const children: IAppointments[] = resp.data;

        // Only the latest request is used; a newer one may have started since.
        if (requestId === latestRequestRef.current) {
          setComponentMounted(false);
          // Axon's count is family-wide: 0 only when the family has no visits
          // at all. Recomputed on every reply so it clears once a visit exists.
          setApptsRecords(
            children.length > 0 && children.every((child) => child.count === 0)
          );
          children.forEach((element: IAppointments, patientIndex: number) => {
            if (element.appointments && Array.isArray(element.appointments)) {
              const filteredAppointments = element.appointments.map(
                (appointment) => {
                  if (
                    appointment.crSyncEnabled === true &&
                    appointment.crStatus in errorMessages &&
                    moment().diff(moment(appointment.createdAt), "minutes") <= 5
                  ) {
                    setSyncActionError(true);
                    setSyncActionMsg(errorMessages[appointment.crStatus]);
                    setSyncActionDate(appointment.startAt);
                  }
                  return {
                    ...appointment,
                    title: appointment.type_name,
                    start: appointment.startAt,
                    end: appointment.endAt,
                    client: appointment.client_name,
                    provider: appointment.provider_name,
                    crStatusCheck: appointment.crStatus,
                    athenaStatusCheck: appointment.athenaStatus,
                    patientIndex,
                  };
                }
              );

              results.push(...filteredAppointments);
            }
          });

          results.sort(
            (a: appointments, b: appointments) =>
              new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
          );

          setAppointments(results);
          cache(CACHED_APPOINTMENTS_KEY, results);
          setBackendError(false);
          setLastUpdated(moment());
        }
      } catch (err) {
        console.error(err, "[Error in getappointments]");
        if (requestId === latestRequestRef.current) setBackendError(true);
      } finally {
        if (requestId === latestRequestRef.current) {
          setsoftloading(false);
          setLoadedRange({ start: startDate, end: endDate });
        }
      }
    },
    [getTokenSilently, user.sub, user.email]
  );

  useEffect(() => {
    const getclinics = async () => {
      try {
        let response: any;
        response = await clientClinics(
          ApiCalls.patientClinics,
          getTokenSilently
        );
        if (response) {
          const clinicArrayData = JSON.parse(response?.data);
          setPatientClinic(
            clinicArrayData?.Clinic_Array_Data.filter(
              (clinic: PatientClinic) => clinic.isActive
            ).map((clinic: PatientClinic) => clinic.clinic)
          );
        }
      } catch (error) {
        setClinicBackendError(true);
        console.error("Error fetching clinics:", error);
      }
    };

    getclinics();
  }, [getTokenSilently]);

  const patientPollData = useCallback(
    (poll: boolean, start: any, end: any, load: boolean) => {
      if (poll) {
        getAppointments(start, end, load);
      } else {
        return;
      }
    },
    [getAppointments]
  );

  useEffect(() => {
    if (backendError) {
      return;
    } else if (pollCount > 25) {
      //keep 123 (30 min)
      setFreezPoll(true);
      return;
    } else {
      if (freezePoll === false && !pdfViewerOpen) {
        // Don't poll when PDF viewer is open
        const interval = setInterval(() => {
          // Double check PDF viewer state before making API call
          if (!pdfViewerOpen) {
            patientPollData(true, StartDate, endDate, false);
            pollCountRef.current += 1;
            setPollCount(pollCountRef.current);
          }
        }, pollingTime);

        return () => clearInterval(interval);
      } else return;
    }
  }, [
    backendError,
    StartDate,
    endDate,
    pollCount,
    patientPollData,
    freezePoll,
    pdfViewerOpen,
  ]);

  const BackEndError = !backendError && !clinicBackendError ? false : true;

  const getAppointmentsCallback = useCallback(getAppointments, [
    getTokenSilently,
    user.email,
    user.sub,
    getAppointments,
  ]);

  const handleDataChange = useCallback(
    (startStr: string, endStr: string) => {
      getAppointmentsCallback(startStr, endStr, true);
      setPollCount(0);
      pollCountRef.current = 0;
      // Picking a day is activity: resume polling and clear the idle banner.
      setFreezPoll(false);
    },
    [getAppointmentsCallback]
  );

  useEffect(() => {
    const currentDate: Date = new Date();
    const weekStart = moment(currentDate).startOf("week").toISOString();
    const weekEnd = moment(currentDate).endOf("week").toISOString();
    handleDataChange(weekStart, weekEnd);
  }, []);

  useEffect(() => {
    const ismobile = isMobile;
    if (ismobile) {
      const currentDate: Date = new Date();
      getAppointmentsCallback(
        moment(currentDate).startOf("day").toISOString(),
        moment(currentDate).endOf("day").toISOString(),
        true
      );
    }
  }, [getAppointmentsCallback]);

  // One list of this caregiver's children for the Welcome line and the child
  // chips. Storage is read once per signed-in user, then merged with each load.
  const [clients, setClients] = useState<CaregiverClient[]>([]);
  const clientsRef = useRef<CaregiverClient[]>([]);
  const clientsUserRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const userId = user?.sub;
    if (!userId) return;
    const isNewUser = clientsUserRef.current !== userId;
    const base = isNewUser ? readCaregiverClients(userId) : clientsRef.current;
    clientsUserRef.current = userId;
    const next = mergeCaregiverClients(base, appointments);
    if (!isNewUser && next === clientsRef.current) return;
    clientsRef.current = next;
    setClients(next);
    writeCaregiverClients(userId, next);
  }, [appointments, user?.sub]);

  const childNames = useMemo(() => clients.map((client) => client.name), [clients]);

  if (loading || !user) {
    return <Loader />;
  }

  // Resets the poll budget (the ref too, or the next tick re-freezes at once)
  // and fetches straight away instead of waiting for the next tick.
  const handleIdleRefresh = () => {
    setPollCount(0);
    pollCountRef.current = 0;
    setFreezPoll(false);
    patientPollData(true, StartDate, endDate, false);
  };

  const scheduleSection = BackEndError ? (
    <ClientRescheduleError BackEndError={BackEndError} onContactUs={openContact} />
  ) : noApptsRecord ? (
    <MessageCard title="No upcoming appointments" icon={<EventOutlinedIcon />}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We don't have any appointments for you at this time. We'd like to get
        you scheduled.
      </Typography>
      <Button
        variant="outlined"
        startIcon={<MailOutlinedIcon />}
        onClick={openContact}
        sx={{ minHeight: 44 }}
      >
        Contact us to schedule
      </Button>
    </MessageCard>
  ) : !hasComponentMounted ? (
    <>
      {syncActionError ? (
        <ClientRescheduleError
          syncActionError={syncActionError}
          syncActionMsg={syncActionMsg}
          syncActionDate={syncActionDate}
        />
      ) : null}
      <CalenderView
        appointments={appointments}
        handleDataChange={handleDataChange}
        softloading={softloading}
        patientClinic={patientClinic}
        pollCount={pollCount}
        displayBanner={displayBanner}
        onContactUs={openContact}
        clients={clients}
        loadedRange={loadedRange}
      />
    </>
  ) : (
    <Paper
      sx={{ p: 3 }}
      role="status"
      aria-busy="true"
      aria-label="Loading appointments"
    >
      <Skeleton variant="text" width={140} height={32} />
      <Skeleton variant="rounded" height={52} sx={{ my: 2 }} />
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={64} />
        <Skeleton variant="rounded" height={64} />
        <Skeleton variant="rounded" height={64} />
      </Stack>
    </Paper>
  );

  return (
    <PageFrame>
      <Stack spacing={3} sx={{ pb: { xs: 2, md: 4 } }}>
        <Box>
          <Typography variant="h5" component="h1">
            Welcome
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {childNames.length > 0
              ? `Here's what's coming up for ${formatNameList(childNames)}.`
              : "Here's what's coming up for your family."}
          </Typography>
        </Box>

        {freezePoll && (
          <Alert
            severity="info"
            action={
              <Button color="inherit" onClick={handleIdleRefresh} sx={{ minHeight: 44 }}>
                Refresh
              </Button>
            }
          >
            Your schedule stopped updating by itself because the page was open for
            a while.
            {lastUpdated && ` Last updated at ${lastUpdated.format(LAST_UPDATED_FORMAT)}.`}
          </Alert>
        )}

        {scheduleSection}

        {/* Side by side only when each card has room; the sidebar takes ~240px. */}
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {!BackEndError && (
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 3, height: "100%" }}>
                <ClientDocuments clientEmail={user.email} />
              </Paper>
            </Grid>
          )}
          <Grid size={{ xs: 12, lg: BackEndError ? 12 : 6 }}>
            <HelpCard onContactUs={openContact} />
          </Grid>
        </Grid>

        {showClientActivities && <ClientActivities />}

      </Stack>
      <ContactDrawer open={contactOpen} onClose={() => setContactOpen(false)} />
    </PageFrame>
  );
};

export default React.memo(Appointment);
